import { useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLoaderData, useLocation, useNavigate } from 'react-router';
import { JsonLd } from 'react-schemaorg';
import { pushToDataLayer } from '../../common/analytics';
import { paths } from '../../common/constants/pathVariables';
import { t } from '../../common/i18n';
import { deepEqual } from '../../common/utils/objectUtils';
import { SiteContext } from '../../public/site/SiteContext';
import { Grid } from '../Grid/Grid';
import { getViewItemListEventForPlaceOrder } from '../PlaceOrder/utils/placeOrderAnalyticsUtils';
import { addToShoppingBasket } from '../ShoppingBasket/shoppingBasketFunctions';
import { handleChangeOffer } from './OrderUtils';
import { MobileCarouselWrapper, getItemList, getOffer } from './SubscriptionUtils';
import {
  PuheBasic,
  PuheLite100m,
  PuheLite300m,
  PuheLite50m,
  PuhePlus,
  PuhePremium,
  PuhePro,
} from './VoiceSubscriptionCards';
import type { ModalDescriptionItem } from './SubscriptionModal';
import type { SubCardWithAnalyticsV4Props } from './subscriptionCardsAnalyticsUtils';
import type { OrderProps, PublicPageOrderProps, SubscriptionCardWithModel } from './types';
import type { OrderVoiceSubLoaderData, SubscriptionUpdateLoaderData } from '../../common/loaders';
import type { CLPopoverImperativeRefHandle } from '../../common/types/componentLibrary';
import type { ChildOrderItem } from '../../generated/api/childOrderItem';
import type { State } from '../../selfservice/common/store';
import type { OfferProductMapType } from '../OrderSubscriptionSelection/OrderSubscriptionSelectionUtils';
import type { ItemList } from 'schema-dts';

export interface HorizontalSubscriptionsProps {
  subscriptionCards: SubscriptionCardWithModel[];
  wide?: boolean;
  compact?: boolean;
  analyticsListName: string;
  isChangeOffer: boolean;
  offerAndFeeMap?: OfferProductMapType[];
  hideNumberSelection?: boolean;
  showAddonConfiguration?: boolean;
  onCardClick?: (card: SubscriptionCardWithModel, modalDescriptionItems?: ModalDescriptionItem[]) => void;
  hideGroupHeaders?: boolean;
  onSelectOffer?: (guid: string, name: string) => void;
  selectedOfferPerGroup?: Record<string, string>;
}

export const HorizontalVoiceSubscriptions = ({
  subscriptionCards,
  wide = false,
  compact = false,
  analyticsListName,
  isChangeOffer,
  offerAndFeeMap = [],
  hideNumberSelection = false,
  showAddonConfiguration = false,
  onCardClick,
  hideGroupHeaders = false,
  onSelectOffer,
  selectedOfferPerGroup,
}: HorizontalSubscriptionsProps) => {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const subscriptionLoaderData = useLoaderData() as SubscriptionUpdateLoaderData;
  const noeLoaderData = useLoaderData() as OrderVoiceSubLoaderData;
  const { siteBaseUrl } = useContext(SiteContext);
  const sharedProps = {
    isChangeOffer,
    analyticsListName,
    showFixedTermInfo: true,
    hideNumberSelection,
    showAddonConfiguration,
    onCardClick,
    compact,
  };
  const activePopoverRef = useRef<CLPopoverImperativeRefHandle>(null);

  const hasRunOnceRef = useRef(false);
  if (!hasRunOnceRef.current && subscriptionCards.every(subCard => subCard.onlineModel?.onlineModelName)) {
    const analyticsEvent = getViewItemListEventForPlaceOrder(
      subscriptionCards as SubCardWithAnalyticsV4Props[],
      analyticsListName
    );
    pushToDataLayer(analyticsEvent);
    hasRunOnceRef.current = true;
  }

  const { campaignsFromVoucher = [], campaignContextsFromVoucher = [] } = useSelector((reduxState: State) => {
    const campaigns = reduxState.campaigns;
    return {
      campaignsFromVoucher: campaigns?.campaigns,
      campaignContextsFromVoucher: campaigns?.campaignContexts,
      voucher: campaigns?.voucher,
    };
  }, deepEqual);

  const onSalesProductOrder = ({ onlineModel, quantity, isAuthenticated, offerGuid }: PublicPageOrderProps) => {
    if (!offerGuid) {
      return;
    }
    const offer = getOffer(offerGuid, onlineModel.offers);
    if (offer) {
      addToShoppingBasket(onlineModel, offer, offer.commercialProducts[0], isAuthenticated, quantity, []);
    }
  };

  const onOrder = (
    { onlineModel, offerGuid, quantity, voucherCode, services }: OrderProps,
    addons?: ChildOrderItem[]
  ) => {
    const addonCodes = addons?.length ? addons.map(a => a.addOnCode) : [];
    if (!isChangeOffer) {
      const offer = getOffer(offerGuid, onlineModel.offers);
      if (!offer) {
        return;
      }
      const commercialProduct = offer.commercialProducts[0];

      // Pass modal addons if provided; addToShoppingBasket always merges bundled
      // offer addons (isOfferAddOn=true) internally, so no manual merge needed here.

      addToShoppingBasket(onlineModel, offer, commercialProduct, true, quantity, addonCodes, voucherCode);
    } else {
      // Change offer flow
      handleChangeOffer({
        onlineModel,
        navigate,
        search,
        quantity,
        voucherCode,
        services,
        selectedAddonCodes: addonCodes,
        offerAndFeeMap,
        campaignsFromVoucher,
        campaignContextsFromVoucher,
        noeOfferCode: offerGuid,
        addOnRules: noeLoaderData.addOnRules,
        subscription: subscriptionLoaderData.subscription,
        confirmationPath: paths.PS_MOBILE_SUBSCRIPTION_UPDATE_CONFIRMATION,
      });
    }
  };

  const onOrderClick = isChangeOffer ? onOrder : onSalesProductOrder;

  let aIndex = 0;
  const availableCardsList = subscriptionCards.map(subscriptionCard => subscriptionCard.cardName || '');
  const cards5gPlus = [
    { name: 'puheplus', component: PuhePlus },
    { name: 'puhepro', component: PuhePro },
    { name: 'puhepremium', component: PuhePremium },
  ].filter(card => availableCardsList.includes(card.name));
  const cards5g = [{ name: 'puhebasic', component: PuheBasic }].filter(card => availableCardsList.includes(card.name));
  const liteCards = [
    { name: 'puhelite50m', component: PuheLite50m },
    { name: 'puhelite100m', component: PuheLite100m },
    { name: 'puhelite300m', component: PuheLite300m },
  ].filter(card => availableCardsList.includes(card.name));

  const content = (
    <div className="of-subscriptions-main-container">
      {hideGroupHeaders ? (
        <div className="of-subscriptions-main-container-header-spacer" />
      ) : (
        <h2 className="of-subscriptions-main-container-carousel-title">{t.Y6ZI('Comprehensive 5G+ subscriptions')}</h2>
      )}
      {cards5gPlus.length > 0 && (
        <MobileCarouselWrapper>
          {cards5gPlus.map(card => {
            const Component = card.component;
            return (
              <Component
                key={card.name}
                analyticsIndex={aIndex++}
                subscriptionCards={subscriptionCards}
                sharedProps={sharedProps}
                onOrderClick={onOrderClick}
                activePopoverRef={activePopoverRef}
                onSelectOffer={onSelectOffer}
                selectedOfferPerGroup={selectedOfferPerGroup}
              />
            );
          })}
        </MobileCarouselWrapper>
      )}
      {!hideGroupHeaders && (
        <h2 className="of-subscriptions-main-container-carousel-title">{t.PN6W('5G subscriptions')}</h2>
      )}
      {cards5g.length > 0 && (
        <MobileCarouselWrapper>
          {cards5g.map(card => {
            const Component = card.component;
            return (
              <Component
                key={card.name}
                analyticsIndex={aIndex++}
                subscriptionCards={subscriptionCards}
                sharedProps={sharedProps}
                onOrderClick={onOrderClick}
                activePopoverRef={activePopoverRef}
                onSelectOffer={onSelectOffer}
                selectedOfferPerGroup={selectedOfferPerGroup}
              />
            );
          })}
        </MobileCarouselWrapper>
      )}
      {hideGroupHeaders && liteCards.length > 0 && (
        <MobileCarouselWrapper>
          {liteCards.map(card => {
            const Component = card.component;
            return (
              <Component
                key={card.name}
                analyticsIndex={aIndex++}
                subscriptionCards={subscriptionCards}
                sharedProps={sharedProps}
                onOrderClick={onOrderClick}
                activePopoverRef={activePopoverRef}
                onSelectOffer={onSelectOffer}
                selectedOfferPerGroup={selectedOfferPerGroup}
              />
            );
          })}
        </MobileCarouselWrapper>
      )}
      <JsonLd<ItemList> item={getItemList(subscriptionCards, siteBaseUrl, pathname)} />
    </div>
  );

  if (compact) {
    return content;
  }

  return <Grid colWidthL={wide ? 12 : 10}>{content}</Grid>;
};
